import {
    Box,
    Button, Chip,
    Dialog, DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RestoreIcon from "@mui/icons-material/Restore";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import IconButton from "@mui/material/IconButton";
import {LoadingButton} from "@mui/lab";
import {
    useAcceptBalanceMutation,
    useDeleteAllBundleMutation,
    useDeleteBundleMutation,
    useGetPlatformsInfoQuery,
    useLazyGetBalanceHashQuery
} from "../../core/reducers/campaign";
import Loading from "../Loading";
import dayjs from "dayjs";
import {isAdmin, isStageAdmin, LIVEOPS_ENV, PROD} from "../Utils/CommonUtils";
import {useSelector} from "react-redux";
import {getDomainAlias} from "../../constants";
import CommonDeleteModal from "../AdminPanel/CommonDeleteModal";
import React, {useContext, useState} from "react";
import BalanceValidationReport, {isBalanceValidationEnabled} from "./BalanceValidationReport";
import {SnackBarContext} from "../../core/context/snackBarProvider";

const shortHash = (hash) => hash ? hash.substring(0, 8) : '—'

// saved on upload balance hash of the generation and the current one after the check
const BalanceCell = ({data, balance}) => {
    if (!data?.balanceLink) return '—'
    return <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap'}}>
        <Tooltip title={data.balanceLink}>
            <IconButton aria-label="Open balance sheet" component="a" href={data.balanceLink} target="_blank"
                        rel="noopener noreferrer" size="small" sx={{p: 0}}>
                <OpenInNewIcon sx={{fontSize: 16}}/>
            </IconButton>
        </Tooltip>
        <Tooltip title={`Saved on upload: ${data.balanceHash ?? 'none'}`}>
            <code>{shortHash(data.balanceHash)}</code>
        </Tooltip>
        {balance && <>
            <span>→</span>
            <Tooltip title={`Current: ${balance.currentHash ?? `can't read the sheet${balance.error ? ': ' + balance.error : ''}`}`}>
                <code>{shortHash(balance.currentHash)}</code>
            </Tooltip>
            {balance.currentHash == null
                ? <Chip size="small" label="unreadable" color="warning" variant="outlined"/>
                : balance.changed
                    ? <Chip size="small" label="changed" color="error"/>
                    : balance.savedHash == null
                        ? <Chip size="small" label="no saved hash" variant="outlined"/>
                        : <Chip size="small" label="same" color="success" variant="outlined"/>}
        </>}
    </Box>
}

export default function CampaignBundleDialog({bundleItem, open, setOpen, campaign, env, selectBundle, fixedBundle, isTemplate}) {
    const userSession = useSelector((state) => state.data.UserSession);
    const {addAlert} = useContext(SnackBarContext);
    const showBalanceValidation = isBalanceValidationEnabled(getDomainAlias());
    // balance hashes are checked against fixed bundles of prod campaigns
    const showBalanceCheck = showBalanceValidation && env == PROD && fixedBundle && !isTemplate;

    const [selectedItem, setSelectedItem] = useState({generation:''})
    const [showDeleteDialog, setShowDeleteDialog] = useState(false)
    const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false)
    const [deleteBundle, deleteBundleResponse] = useDeleteBundleMutation()
    const [deleteAllBundle, deleteAllBundleResponse] = useDeleteAllBundleMutation()
    const [getBalanceHash] = useLazyGetBalanceHashQuery()
    const [checking, setChecking] = useState(false)
    const [acceptBalance, acceptBalanceResponse] = useAcceptBalanceMutation()
    const [balances, setBalances] = useState(null)

    const platformInfo = useGetPlatformsInfoQuery({env, campaign}, {skip: !open});

    const formatdate = (timestamp) => {
        if(timestamp == null) return ''
        let d = dayjs(timestamp * 1000).utcOffset(180).format('YYYY-MM-DD HH:mm')
        return d;
    }

    const sortedGenerations = platformInfo.data!=undefined ? [...platformInfo.data.generations].sort((a, b) => b.generation.generation - a.generation.generation) : null;

    const showRollback = () => {
        if(env == PROD) return false
        if(env == LIVEOPS_ENV) return isAdmin(userSession)
        return isAdmin(userSession) || isStageAdmin(userSession)
    }

    const showDelete = () => {
        if(getDomainAlias() === 'cross-staging'
            || getDomainAlias() === 'cross-toh-test'
            || getDomainAlias() === 'cm'
            || getDomainAlias().indexOf("cross")===0) return true;

        if(getDomainAlias() !== 'toh'
            && getDomainAlias() !== 'ds'
            && getDomainAlias() !== 'mc'
            && getDomainAlias() !== 'local'
        ) return false;
        return isAdmin(userSession)
    }

    const showActions = showRollback() || showDelete()

    // saved on upload hashes are in the generations, current ones are read by stage once per link
    const handleCheckBalance = async () => {
        setChecking(true)
        const generations = (sortedGenerations ?? []).filter(item => item.generation?.data?.balanceLink)
        const links = [...new Set(generations.map(item => item.generation.data.balanceLink))]
        const responses = await Promise.all(links.map(link => getBalanceHash({link})))
        const hashByLink = Object.fromEntries(links.map((link, i) => [link, responses[i].data?.hash ?? null]))
        const errorByLink = Object.fromEntries(links.map((link, i) => [link, responses[i].data?.error
            ?? responses[i].error?.data?.message ?? null]))
        const failed = responses.find(response => response.error)
        if (failed) addAlert({text: 'Error ' + (failed.error?.data?.message ?? ''), type: 'error'})
        setBalances(generations.map(item => {
            const savedHash = item.generation.data.balanceHash ?? null
            const currentHash = hashByLink[item.generation.data.balanceLink]
            return {
                generation: item.generation.generation,
                balanceLink: item.generation.data.balanceLink,
                savedHash,
                currentHash,
                error: currentHash == null ? errorByLink[item.generation.data.balanceLink] : null,
                changed: savedHash != null && currentHash != null && savedHash !== currentHash
            }
        }))
        setChecking(false)
    }

    const handleAcceptBalance = async () => {
        const response = await acceptBalance({env, campaign})
        if (response.data) {
            setBalances(response.data)
            addAlert({text: 'Balance accepted', type: 'success'})
        } else addAlert({text: 'Error ' + (response.error?.data?.message ?? ''), type: 'error'})
    }

    const balanceByGeneration = Object.fromEntries((balances ?? []).map(balance => [balance.generation, balance]))
    const changedCount = (balances ?? []).filter(balance => balance.changed).length

    return (
        <>
            <Dialog
                onClose={() => setOpen(false)} open={open}
                maxWidth={'lg'}
            >
                <DialogTitle sx={{py: 1.5, pr: 6}}>
                    Bundle {bundleItem?.id} {fixedBundle&&<Chip size="small" label="fixedBundle" />}
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={() => setOpen(false)}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon/>
                </IconButton>
                <DialogContent sx={{pt: 0}}>
                    {showBalanceCheck && <Box className="balance-check" sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                        <LoadingButton size="small" variant="outlined" loading={checking}
                                       onClick={handleCheckBalance}>
                            Check balance
                        </LoadingButton>
                        {balances && <Typography variant="body2" color={changedCount > 0 ? 'error.main' : 'success.main'}>
                            {changedCount > 0 ? `Changed since upload: ${changedCount}` : 'Balance not changed'}
                        </Typography>}
                        {changedCount > 0 && isAdmin(userSession) &&
                            <Tooltip title="Save the current hashes for all campaigns with this bundle">
                                <LoadingButton size="small" variant="contained" color="warning"
                                               loading={acceptBalanceResponse.isLoading} onClick={handleAcceptBalance}>
                                    It's OK
                                </LoadingButton>
                            </Tooltip>}
                    </Box>}
                    <Loading isLoading={platformInfo.isLoading} isError={platformInfo.isError} error={platformInfo.error}>
                        {platformInfo.data != undefined &&
                            <Table size="small" sx={{'& td, & th': {fontSize: 12, px: 1, py: 0.5}}}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Gen</TableCell>
                                        <TableCell>Client version</TableCell>
                                        <TableCell>Bundle version</TableCell>
                                        <TableCell>Branches</TableCell>
                                        <TableCell>Platforms</TableCell>
                                        <TableCell>Created</TableCell>
                                        {showBalanceValidation&&<TableCell>Validation</TableCell>}
                                        {showBalanceValidation&&<TableCell>Balance</TableCell>}
                                        {showActions&&<TableCell/>}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sortedGenerations
                                        .map((item, key) => (

                                        <TableRow key={key}>
                                            <TableCell><b>G{item.generation.generation}</b></TableCell>
                                            <TableCell sx={{whiteSpace: 'nowrap'}}>{item.generation['clientVersion']['version']}</TableCell>
                                            <TableCell sx={{whiteSpace: 'nowrap'}}>{item.generation['bundleVersion']}</TableCell>
                                            <TableCell>
                                                <div>main: {item.generation?.data?.repositoryBranch}</div>
                                                <div>content: {item.generation?.data?.contentBranch}</div>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25}}>
                                                    {Object.keys(item.platforms).map(platform =>
                                                        <Chip key={platform} size="small" label={platform}
                                                              variant="outlined"
                                                              color={item.platforms[platform] ? 'success' : 'default'}
                                                              sx={{height: 18, fontSize: 11, '& .MuiChip-label': {px: 0.75},
                                                                  ...(!item.platforms[platform] && {color: 'text.disabled', textDecoration: 'line-through'})}}/>)}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{whiteSpace: 'nowrap'}}>{formatdate(item.generation?.data?.date)}</TableCell>
                                            {showBalanceValidation&&<TableCell>
                                                <BalanceValidationReport
                                                    status={item.generation?.data?.balanceValidationStatus}
                                                    buildLink={item.generation?.data?.balanceValidationBuildLink}
                                                />
                                            </TableCell>}
                                            {showBalanceValidation&&<TableCell>
                                                <BalanceCell data={item.generation?.data}
                                                             balance={balanceByGeneration[item.generation.generation]}/>
                                            </TableCell>}
                                            {showActions&&<TableCell sx={{whiteSpace: 'nowrap'}}>
                                                {showRollback()&&<Tooltip title="Rollback">
                                                    <IconButton size="small" color="primary" onClick={() => selectBundle(key, item)}>
                                                        <RestoreIcon fontSize="small"/>
                                                    </IconButton>
                                                </Tooltip>}
                                                {showDelete()&&<Tooltip title={`Delete in ${env}`}>
                                                    <IconButton size="small" color="warning" onClick={() => {
                                                        setSelectedItem({generation: item.generation.generation, bundle: bundleItem.id, environment: env})
                                                        setShowDeleteDialog(true)
                                                    }}>
                                                        <DeleteOutlineIcon fontSize="small"/>
                                                    </IconButton>
                                                </Tooltip>}
                                                {showDelete()&&<Tooltip title="Delete in ALL envs">
                                                    <IconButton size="small" color="error" onClick={() => {
                                                        setSelectedItem({generation: item.generation.generation, bundle: bundleItem.id})
                                                        setShowDeleteAllDialog(true)
                                                    }}>
                                                        <DeleteForeverIcon fontSize="small"/>
                                                    </IconButton>
                                                </Tooltip>}
                                            </TableCell>}
                                        </TableRow>

                                    ))}
                                </TableBody>
                            </Table>
                        }

                    </Loading>
                </DialogContent>
                <DialogActions>

                    <Button variant="contained" size="small"
                            onClick={() => setOpen(false)}>
                        Close
                    </Button>

                </DialogActions>
            </Dialog>
            {showDeleteDialog&&<CommonDeleteModal
                title={<>Delete in <b>{env}</b> Bundle {selectedItem.bundle} G{selectedItem.generation}?</>}
                open={showDeleteDialog}
                close={() => {
                    setShowDeleteDialog(false)
                }}
                removeEnvironment={deleteBundle}
                response={deleteBundleResponse}
                currentEnvName={selectedItem}
            />}
            {showDeleteAllDialog&&<CommonDeleteModal
                title={<>Delete in <b>ALL ENVS</b> Bundle {selectedItem.bundle} G{selectedItem.generation}?</>}
                open={showDeleteAllDialog}
                close={() => {
                    setShowDeleteAllDialog(false)
                }}
                removeEnvironment={deleteAllBundle}
                response={deleteAllBundleResponse}
                currentEnvName={selectedItem}
            />}
        </>
    );
}
